import React, { useState , useEffect , useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { getRequest, postRequest, putRequest } from '../../../lib/api'; // Ensure this method exists
import { AuthContext } from '../../../authContext/context';
// Redux imports
import { useDispatch, useSelector } from 'react-redux';
import { updateCustomer } from '../../../redux/slices/customerSlice';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import { Check } from 'lucide-react';
import Modal from 'react-bootstrap/Modal';
import EmailIcon from '@mui/icons-material/Email';

import Table from 'react-bootstrap/Table';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import { NavLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select  from '@mui/material/Select';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import BreadcrumbsWrapper from '../../../components/breadcrumbs'
import AddIcon from '@mui/icons-material/Add';
import Typography from '@mui/material/Typography';

function MyVerticallyCenteredModal({ show, onHide, authToken, onCustomerAdd }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounceRef = useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateCustomer = async () => {
    // Check if at least email or phone is provided
    if (!searchFields.email && !searchFields.phone_number) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please provide at least an email or phone number to create a customer.'
      });
      return;
    }

    setCreating(true);
    try {
      const payload = {
        title: searchFields.title || '',
        forename: searchFields.forename || '',
        surname: searchFields.surname || '',
        email: searchFields.email || '',
        phone_number: searchFields.phone_number || ''
      };

      const res = await postRequest('private/createCustomer', payload, authToken);
      // Check if customer data exists (status can be 'success' or 200)
      if (res?.data && res.data._id) {
        Swal.fire({
          icon: 'success',
          title: 'Customer Created',
          text: 'Customer has been created successfully!',
          timer: 2000,
          showConfirmButton: false
        });
        onCustomerAdd(res.data);
        onHide();
      } else if ((res?.status === 'success' || res?.status === 200) && res?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Customer Created',
          text: 'Customer has been created successfully!',
          timer: 2000,
          showConfirmButton: false
        });
        onCustomerAdd(res.data);
        onHide();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: res?.message || 'Failed to create customer'
        });
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err?.message || 'Something went wrong'
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Customers</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          Add
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className='text-center'>
                      <div style={{ padding: '20px' }}>
                        <div style={{ marginBottom: '15px', color: '#666' }}>
                          No customers found
                        </div>
                        {(searchFields.email || searchFields.phone_number) && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateCustomer}
                            disabled={creating}
                            sx={{
                              backgroundColor: '#6f42c1',
                              '&:hover': {
                                backgroundColor: '#5a32a3'
                              }
                            }}
                          >
                            {creating ? 'Creating...' : 'ADD CUSTOMER'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}





export default function EditCustomer() {
   const { lead_id, slug  , id} = useParams();
  const navigate = useNavigate();
  const authCtx = React.useContext(AuthContext);
  const [loading, setLoading] = React.useState(false);
    const [customerError, setCustomerError] = useState(false);
   const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [groupCodes, setGroupCodes] = useState([]);
       const [productId, setProductId] = useState('');
       const [lenderPanels, setLenderPanels] = useState([]);
       const [partners, setPartners] = useState([]);


const {
  register,
  handleSubmit,
  reset,
  control,
  formState: { errors },
  setValue,
} = useForm({
  defaultValues: {
    group_code: '',
    acquisition: '',
    net_value: '',
    lender_panel_id: '',
    partner_id: '',
    documents: {
      health_welfare: false,
      property_finance: false,
    },
  },
});


  const customerList = useSelector((state) => state?.customer?.list);
  const customerData = customerList?.find((b) => b._id === id);

  // Populate form if branch data found in Redux
  React.useEffect(() => {
    if (customerData) {
      reset({
        title: customerData?.title,
        forename: customerData?.forename,
        surname: customerData?.surname,
        dob: customerData?.dob,
        email: customerData?.email,
        phone_number: customerData?.phone_number,
        address1: customerData.address?.address1 || '',
        address2: customerData.address?.address2 || '',
        town: customerData.address?.town || '',
        country: customerData.address?.country || '',
        postcode: customerData.address?.postcode || '',
      });
    }
  }, [customerData, reset]);


  const onSubmit = async (formData) => {
    if (selectedCustomers.length === 0) {
      setCustomerError(true);
      return;
    }

    // Convert documents object to array of strings
    const documentsArray = [];
    if (formData.documents?.health_welfare) {
      documentsArray.push("Health & Welfare");
    }
    if (formData.documents?.property_finance) {
      documentsArray.push("Property & Finance");
    }

    const payload = {
      ...formData,
      documents: documentsArray,
      product_id: productId,
      created_id: localStorage.getItem('id'),
      customer_ids: selectedCustomers.map((c) => c._id),
      lender_panel_id: formData.lender_panel_id || null,
      partner_id: formData.partner_id || null,
    };

    try {
      const res = await postRequest(`private/lead-edit/${lead_id}`, payload, authCtx.token);
      if (res?.status === 'success') {
        Swal.fire('Success', 'Lead updated successfully!', 'success');
        navigate('/leads');
      } else {
        Swal.fire('Error', res?.message || 'Update failed', 'error');
      }
    } catch (err) {
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  const [basicSelections, setBasicSelections] = useState([]);


  const slugToTitle = (slug) => {
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

  const readableTitle = slugToTitle(slug || '');


  const [modalShow, setModalShow] = React.useState(false);
    const [age, setAge] = React.useState('');

  const handleChange = (event) => {
    setAge(event.target.value);
  };
   const fetchLeadDetails = async () => {
      try {
        const res = await postRequest(`private/get-single-lead/${lead_id}`, {}, authCtx.token);
        const lead = res?.data;
        console.log(lead, 'lead details----');
  
  
     if (lead) {
  reset({
    group_code: lead.group_code?._id ? String(lead.group_code._id) : '',
    acquisition: lead.acquisition || '',
    net_value: lead.net_value || '',
    lender_panel_id: lead.lender_panel_id?._id ? String(lead.lender_panel_id._id) : '',
    partner_id: lead.partner_id?._id ? String(lead.partner_id._id) : '',
    documents: {
      health_welfare: !!lead.documents?.includes("Health & Welfare"),
      property_finance: !!lead.documents?.includes("Property & Finance"),
    },
  });

  setSelectedCustomers(lead.customers || []);
  setProductId(lead.product_id?._id || '');
}

     
      } catch (err) {
        console.error('Error fetching lead:', err);
      }
    };

      const fetchGroupCodes = async () => {
        try {
          const res = await postRequest('private/getGroupCodeList', {}, authCtx.token);
          if (res?.status === 200 && Array.isArray(res.data)) {
            setGroupCodes(res.data);
          }
        } catch (err) {
          console.error('Error fetching group codes:', err);
        }
      };

      // Fetch active lender panels
      const fetchActiveLenderPanels = async () => {
        try {
          const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
          if (response?.status === 200 && response?.data) {
            setLenderPanels(response.data);
          }
        } catch (error) {
          console.error('Error fetching lender panels:', error);
        }
      };

      // Fetch partners
      const fetchPartners = async () => {
        try {
          const response = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
          if (response?.status === 200 && response?.data?.docs) {
            setPartners(response.data.docs);
          }
        } catch (error) {
          console.error('Error fetching partners:', error);
        }
      };
  
    useEffect(() => {
      fetchGroupCodes();
      fetchActiveLenderPanels();
      fetchPartners();
      fetchLeadDetails();
    }, [lead_id]);
  return (
    <>
      <BreadcrumbsWrapper
              items={[
                { label: 'Dashboard', path: '/dashboard' },
                { label: 'Leads', path: '/leads' },
                { label: 'Lasting Power of attorney', path: '' },
              ]}
            />
            <br />
            <br />
      <div className="page-header pb-3">
      
        <h1 className="mt-0 mb-0">Edit {readableTitle} #{lead_id}</h1>
      </div>

      <div className="page-top-formview mb-3 edit-lead">
       
        <div className='card-body'>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="mb-3">
            <Form.Group as={Col} controlId="group_code">
  <Controller
    name="group_code"
    control={control}
    rules={{ required: 'Group Code is required' }}
    render={({ field }) => (
      <FormControl fullWidth sx={{ my: 1 }} error={!!errors.group_code}>
        <InputLabel>Group Code</InputLabel>
        <Select {...field} label="Group Code">
          {groupCodes.map((item) => (
            <MenuItem key={item._id} value={String(item._id)}>
              {`${item.name} (${item.code})`}
            </MenuItem>
          ))}
        </Select>
        {errors.group_code && (
          <p style={{ color: 'red', fontSize: '0.8rem' }}>{errors.group_code.message}</p>
        )}
      </FormControl>
    )}
  />
</Form.Group>

<Form.Group as={Col} controlId="acquisition">
  <Controller
    name="acquisition"
    control={control}
    rules={{ required: 'Acquisition is required' }}
    render={({ field }) => (
      <FormControl fullWidth sx={{ my: 1 }} error={!!errors.acquisition}>
        <InputLabel>Acquisition</InputLabel>
        <Select {...field} label="Acquisition">
          <MenuItem value="Inbound">Inbound</MenuItem>
          <MenuItem value="Cold-Acquisition">Cold-Acquisition</MenuItem>
        </Select>
        {errors.acquisition && (
          <p style={{ color: 'red', fontSize: '0.8rem' }}>{errors.acquisition.message}</p>
        )}
      </FormControl>
    )}
  />
</Form.Group>


            </Row>

            {/* Lender Panel and Partner Section */}
            <Row className="mb-3">
              <Form.Group as={Col} controlId="lender_panel_id">
                <Controller
                  name="lender_panel_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth sx={{ my: 1 }}>
                      <InputLabel>Lender Panel</InputLabel>
                      <Select {...field} label="Lender Panel" displayEmpty>
                        <MenuItem value="">
                          <em>Select Lender Panel (Optional)</em>
                        </MenuItem>
                        {lenderPanels.map((panel) => (
                          <MenuItem key={panel._id} value={String(panel._id)}>
                            {panel.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Form.Group>

              <Form.Group as={Col} controlId="partner_id">
                <Controller
                  name="partner_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth sx={{ my: 1 }}>
                      <InputLabel>Partner</InputLabel>
                      <Select {...field} label="Partner" displayEmpty>
                        <MenuItem value="">
                          <em>Select Partner (Optional)</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner._id} value={String(partner._id)}>
                            {partner.name || 'Unknown'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Form.Group>
            </Row>

            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Customers
            </Typography>
<Row className="mb-3">
  <Box sx={{ mb: 3 }}>
    {selectedCustomers.length > 0 ? (
      selectedCustomers.map((customer) => (
        <Box
          key={customer._id}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            mb: 2,
            p: 1.5,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            backgroundColor: '#f9f9f9'
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {customer.title} {customer.forename} {customer.surname}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {customer.email && (
                <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                  📧 {customer.email}
                </Typography>
              )}
              {customer.phone_number && (
                <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                  📞 {customer.phone_number}
                </Typography>
              )}
            </Box>
          </Box>
          <Button
            size="small"
            onClick={() =>
              setSelectedCustomers((prev) =>
                prev.filter((c) => c._id !== customer._id)
              )
            }
            sx={{
              minWidth: 'auto',
              color: '#d32f2f',
              '&:hover': {
                backgroundColor: '#ffebee'
              }
            }}
          >
            ✕
          </Button>
        </Box>
      ))
    ) : (
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
        <Typography variant="body2" sx={{ color: '#666' }}>
          No customers added yet. Click "Add Customer" to add customers.
        </Typography>
      </Box>
    )}
    <Button
      variant="text"
      startIcon={<AddIcon />}
      onClick={() => setModalShow(true)}
      sx={{ color: '#bb0000', mt: 1 }}
    >
      Add Customer
    </Button>
  </Box>
  <MyVerticallyCenteredModal
    show={modalShow}
    onHide={() => setModalShow(false)}
    authToken={authCtx.token}
    onCustomerAdd={(cust) => {
      if (!selectedCustomers.some((c) => c._id === cust._id)) {
        setSelectedCustomers((prev) => [...prev, cust]);
      }
    }}
  />
</Row>

        
        <Form.Label>Documents</Form.Label>
<Row className="mb-3">
  <FormGroup>
    <Controller
      name="documents.health_welfare"
      control={control}
      render={({ field }) => (
        <FormControlLabel
          control={<Checkbox {...field} checked={field.value} />}
          label="Health & Welfare"
        />
      )}
    />
    <Controller
      name="documents.property_finance"
      control={control}
      render={({ field }) => (
        <FormControlLabel
          control={<Checkbox {...field} checked={field.value} />}
          label="Property & Finance"
        />
      )}
    />
  </FormGroup>
</Row>

        


            <Stack direction="row" spacing={2}>

              <Button variant="contained" className='red-bg red-button text-capitalize ' type="submit" disabled={loading}>
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
              </Button>
            </Stack>
          </Form>
        </div>
      </div>
    </>
  );
}
